"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { getAddress } from "viem";
import { useAccount } from "wagmi";
import { useAuth } from "@/context/auth";
import {
  assertHexAddress,
  type HexAddress,
} from "@/lib/utils/assertHexAddress";

/**
 * Canonicalize a wagmi-provided address (connected wallet) to lowercase HexAddress.
 * Wagmi guarantees checksummed EIP-55 addresses at runtime.
 * Returns undefined if value is falsy; casts and lowercases otherwise.
 * We avoid strict getAddress() re-validation here so test mocks with short addresses
 * still flow through — strict validation only applies to API-sourced target wallets
 * (via assertHexAddress in the impersonation path below).
 */
function safeCanonicalize(value: string | undefined): HexAddress | undefined {
  if (!value) return undefined;
  return value.toLowerCase() as HexAddress;
}

/**
 * Returns the effective address for read operations:
 * - During impersonation with a valid target wallet: returns canonical target address.
 * - Otherwise: returns canonical connected wallet address.
 *
 * Does NOT fire fallback toast — that is useEffectiveAccount's responsibility,
 * which is mounted globally via ImpersonationBanner.
 *
 * Canonicalization: getAddress(v).toLowerCase() for stable display + cache parity (Fix #5).
 */
export function useEffectiveAddress(): HexAddress | undefined {
  const { address: connectedRaw } = useAccount();
  const isImpersonating = useAuth((s) => s.isImpersonating);
  const targetWallet = useAuth((s) => s.user?.wallet_address);

  const canonicalConnected = safeCanonicalize(connectedRaw);

  if (isImpersonating && targetWallet) {
    try {
      const validated = assertHexAddress(targetWallet);
      const canonicalTarget = getAddress(validated).toLowerCase() as HexAddress;
      return canonicalTarget;
    } catch {
      // Fall through to connected — toast handled by useEffectiveAccount (banner-mounted)
      // eslint-disable-next-line no-console
      console.warn(
        "useEffectiveAddress: malformed target wallet_address — falling back to connected wallet",
        { targetWallet },
      );
    }
  }

  return canonicalConnected;
}

/**
 * Extended account info for components that need isConnected / isFallback signals.
 * - isFallback: true when impersonating but target wallet_address is malformed/missing,
 *   and we silently use the connected wallet instead.
 * - Fires toast.warning ONCE on fallback (Sonner id-dedup + useRef guard).
 */
export function useEffectiveAccount(): {
  address: HexAddress | undefined;
  isConnected: boolean;
  isImpersonating: boolean;
  isFallback: boolean;
} {
  const { address: connectedRaw, isConnected } = useAccount();
  const isImpersonating = useAuth((s) => s.isImpersonating);
  const targetWallet = useAuth((s) => s.user?.wallet_address);
  const t = useTranslations("errors");

  const canonicalConnected = safeCanonicalize(connectedRaw);

  // Attempt target resolution
  let targetResolved: HexAddress | null = null;
  let targetFailed = false;

  if (isImpersonating && targetWallet !== undefined && targetWallet !== null) {
    try {
      const validated = assertHexAddress(targetWallet);
      targetResolved = getAddress(validated).toLowerCase() as HexAddress;
    } catch {
      targetFailed = true;
      // eslint-disable-next-line no-console
      console.warn(
        "useEffectiveAccount: malformed target wallet_address — falling back to connected wallet",
        { targetWallet },
      );
    }
  } else if (isImpersonating && !targetWallet) {
    // Empty / undefined wallet_address while impersonating → treat as malformed
    targetFailed = true;
    // eslint-disable-next-line no-console
    console.warn(
      "useEffectiveAccount: missing target wallet_address — falling back to connected wallet",
    );
  }

  // Toast-once on fallback — useRef guard prevents re-firing across re-renders
  const prevFallbackRef = useRef<boolean>(false);
  useEffect(() => {
    if (targetFailed && !prevFallbackRef.current) {
      toast.warning(t("impersonationAddressFallback"), {
        id: "impersonation-address-fallback",
      });
      prevFallbackRef.current = true;
    }
    if (!targetFailed) {
      prevFallbackRef.current = false;
    }
  }, [targetFailed, t]);

  // Return shape
  if (isImpersonating && targetResolved) {
    return {
      address: targetResolved,
      isConnected: true,
      isImpersonating: true,
      isFallback: false,
    };
  }
  if (isImpersonating && targetFailed) {
    return {
      address: canonicalConnected,
      isConnected,
      isImpersonating: true,
      isFallback: true,
    };
  }
  return {
    address: canonicalConnected,
    isConnected,
    isImpersonating: false,
    isFallback: false,
  };
}
